/**
 * FFmpeg.wasm loader and initialization module
 * Handles loading the WASM binary and verifying filter availability
 *
 * NOTE: This file uses ffmpeg.wasm's JavaScript API methods like FFmpeg.exec()
 * which run FFmpeg commands inside the WebAssembly sandbox - NOT Node.js child_process.
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';

// Singleton FFmpeg instance
let ffmpegInstance = null;
let isLoaded = false;
let xfadeAvailable = null;
let acrossfadeAvailable = null;

/**
 * Load and initialize FFmpeg.wasm
 * @param {Function} onLog - Optional callback for FFmpeg log messages
 * @param {Function} onProgress - Optional callback for progress events
 * @returns {Promise<FFmpeg>} The loaded FFmpeg instance
 */
export async function loadFFmpeg(onLog = null, onProgress = null) {
  if (isLoaded && ffmpegInstance) {
    return ffmpegInstance;
  }

  ffmpegInstance = new FFmpeg();

  // Set up log listener if provided
  if (onLog) {
    ffmpegInstance.on('log', onLog);
  }

  // Set up progress listener if provided
  if (onProgress) {
    ffmpegInstance.on('progress', onProgress);
  }

  // Check SharedArrayBuffer availability (required for ffmpeg.wasm)
  if (typeof SharedArrayBuffer === 'undefined' || !self.crossOriginIsolated) {
    const msg = 'SharedArrayBuffer not available. Requires HTTPS/localhost with COOP/COEP headers.';
    throw new Error(msg);
  }

  // Load from CDN with blob URLs to bypass CORS
  // Use ESM build (works with Vite when @ffmpeg packages are excluded from optimizeDeps)
  const baseURL = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/esm';

  try {
    console.log('[FFmpeg] Starting download from CDN...');

    // Download FFmpeg core files via blob URLs (bypasses CORS)
    console.log('[FFmpeg] Downloading ffmpeg-core.js...');
    const coreURL = await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript');
    console.log('[FFmpeg] Downloaded ffmpeg-core.js');

    console.log('[FFmpeg] Downloading ffmpeg-core.wasm (~31MB)...');
    const wasmURL = await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm');
    console.log('[FFmpeg] Downloaded ffmpeg-core.wasm');

    // Initialize FFmpeg with the blob URLs
    // Note: Don't pass classWorkerURL - Vite handles the worker when packages are excluded from optimizeDeps
    console.log('[FFmpeg] Initializing FFmpeg instance...');
    await ffmpegInstance.load({ coreURL, wasmURL });
    console.log('[FFmpeg] FFmpeg loaded successfully!');

    isLoaded = true;
    return ffmpegInstance;
  } catch (error) {
    console.error('[FFmpeg] Load failed:', error);
    ffmpegInstance = null;
    throw new Error(`Failed to load FFmpeg.wasm: ${error.message}`);
  }
}

/**
 * Check if the xfade filter is available in the FFmpeg build
 * Uses ffmpeg.wasm's API to run -filters command inside WASM sandbox
 * @param {FFmpeg} ffmpeg - The loaded FFmpeg instance
 * @returns {Promise<boolean>} True if xfade filter is available
 */
export async function checkXfadeAvailability(ffmpeg) {
  if (xfadeAvailable !== null) {
    return xfadeAvailable;
  }

  let logOutput = '';
  const logHandler = ({ message }) => {
    logOutput += message + '\n';
  };

  ffmpeg.on('log', logHandler);

  try {
    // Run ffmpeg -filters inside WASM sandbox (not shell)
    await ffmpeg.exec(['-filters']);
    xfadeAvailable = logOutput.includes('xfade');
    return xfadeAvailable;
  } catch {
    xfadeAvailable = false;
    return false;
  } finally {
    ffmpeg.off('log', logHandler);
  }
}

/**
 * Check if the acrossfade filter is available in the FFmpeg build
 * Uses ffmpeg.wasm's API to run -filters command inside WASM sandbox
 * @param {FFmpeg} ffmpeg - The loaded FFmpeg instance
 * @returns {Promise<boolean>} True if acrossfade filter is available
 */
export async function checkAcrossfadeAvailability(ffmpeg) {
  if (acrossfadeAvailable !== null) {
    return acrossfadeAvailable;
  }

  let logOutput = '';
  const logHandler = ({ message }) => {
    logOutput += message + '\n';
  };

  ffmpeg.on('log', logHandler);

  try {
    // Run ffmpeg -filters inside WASM sandbox (not shell)
    await ffmpeg.exec(['-filters']);
    acrossfadeAvailable = logOutput.includes('acrossfade');
    return acrossfadeAvailable;
  } catch {
    acrossfadeAvailable = false;
    return false;
  } finally {
    ffmpeg.off('log', logHandler);
  }
}

/**
 * Probe a video file to extract metadata (duration, resolution, audio presence)
 * Uses ffmpeg.wasm's API to run -i command inside WASM sandbox
 * @param {FFmpeg} ffmpeg - The loaded FFmpeg instance
 * @param {File} file - The video file to probe
 * @returns {Promise<{duration: number, width: number, height: number, hasAudio: boolean}>}
 */
export async function probeVideo(ffmpeg, file) {
  const { fetchFile } = await import('@ffmpeg/util');
  const filename = 'probe_input.mp4';

  // Write file to virtual FS
  await ffmpeg.writeFile(filename, await fetchFile(file));

  // Capture log output to extract metadata
  let logOutput = '';
  const logHandler = ({ message }) => {
    logOutput += message + '\n';
  };

  ffmpeg.on('log', logHandler);

  try {
    // Run ffmpeg -i inside WASM sandbox (will "fail" but output metadata to stderr)
    await ffmpeg.exec(['-i', filename]);
  } catch {
    // Expected to fail - we just want the metadata output
  }

  ffmpeg.off('log', logHandler);

  // Parse duration: "Duration: HH:MM:SS.ms"
  const durationMatch = logOutput.match(/Duration:\s*(\d+):(\d+):(\d+)\.(\d+)/);
  let duration = 0;
  if (durationMatch) {
    const hours = parseInt(durationMatch[1], 10);
    const minutes = parseInt(durationMatch[2], 10);
    const seconds = parseInt(durationMatch[3], 10);
    const ms = parseInt(durationMatch[4], 10) / 100;
    duration = hours * 3600 + minutes * 60 + seconds + ms;
  }

  // Parse resolution: "Video: ... 1920x1080" or similar
  const resolutionMatch = logOutput.match(/(\d{2,5})x(\d{2,5})/);
  let width = 0;
  let height = 0;
  if (resolutionMatch) {
    width = parseInt(resolutionMatch[1], 10);
    height = parseInt(resolutionMatch[2], 10);
  }

  // Check for audio stream: look for "Audio:" in the output
  const hasAudio = logOutput.includes('Audio:');

  // Clean up
  try {
    await ffmpeg.deleteFile(filename);
  } catch {
    // Ignore cleanup errors
  }

  if (duration === 0) {
    throw new Error('Could not determine video duration');
  }

  return { duration, width, height, hasAudio };
}

/**
 * Get the current FFmpeg instance (must call loadFFmpeg first)
 * @returns {FFmpeg|null} The FFmpeg instance or null if not loaded
 */
export function getFFmpeg() {
  return ffmpegInstance;
}

/**
 * Check if FFmpeg is loaded
 * @returns {boolean} True if FFmpeg is loaded and ready
 */
export function isFFmpegLoaded() {
  return isLoaded;
}

/**
 * Get xfade availability status
 * @returns {boolean|null} True if available, false if not, null if not checked
 */
export function isXfadeAvailable() {
  return xfadeAvailable;
}

/**
 * Get acrossfade availability status
 * @returns {boolean|null} True if available, false if not, null if not checked
 */
export function isAcrossfadeAvailable() {
  return acrossfadeAvailable;
}
